/**
 * Phase J.3.4 — Customer "Payment methods" page.
 *
 * Lists the customer's saved Stripe ACH bank accounts, plus actions:
 *   - Add new bank account (opens AddBankAccountModal → SetupIntent)
 *   - Set as default
 *   - Remove (detach + cascade)
 *
 * Returns from the SetupIntent redirect (?setup_return=1) trigger a
 * refetch so the newly-saved bank shows up once the webhook persists it.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ChevronLeft, CreditCard, Landmark, Plus, Star, Trash2,
    ShieldCheck, Loader2, AlertCircle,
} from "lucide-react";
import AddBankAccountModal from "@/components/AddBankAccountModal";


function PMRow({ pm, isDefault, onSetDefault, onRemove, busy }) {
    return (
        <div
            data-testid={`pm-row-${pm.id}`}
            className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4"
        >
            <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-900 flex items-center justify-center ring-1 ring-blue-100 shrink-0">
                <Landmark size={18} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">
                        {pm.bank_name || "Bank"} <span className="font-mono text-slate-500">••••{pm.last4}</span>
                    </span>
                    {isDefault && (
                        <span
                            data-testid={`pm-row-default-${pm.id}`}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-900 ring-1 ring-inset ring-emerald-200"
                        >
                            <Star size={11} aria-hidden="true" /> Default
                        </span>
                    )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                    US bank account · Saved {pm.verified_at ? new Date(pm.verified_at).toLocaleDateString() : ""}
                </p>
            </div>
            <div className="flex items-center gap-2">
                {!isDefault && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => onSetDefault(pm.id)}
                        disabled={busy}
                        data-testid={`pm-row-set-default-${pm.id}`}
                    >
                        <Star size={12} className="mr-1.5" /> Make default
                    </Button>
                )}
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onRemove(pm)}
                    disabled={busy}
                    data-testid={`pm-row-remove-${pm.id}`}
                    className="text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-900"
                >
                    <Trash2 size={12} className="mr-1.5" /> Remove
                </Button>
            </div>
        </div>
    );
}


export default function CustomerPaymentMethodsPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [defaultId, setDefaultId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/customer/payment-methods");
            setItems(data?.items || []);
            setDefaultId(data?.default_payment_method_id || null);
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Re-fetch when returning from SetupIntent redirect.
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        if (sp.get("setup_return") === "1") {
            toast.success("Bank account verification submitted. It'll appear here once Stripe finishes verifying it.");
            // Poll a few times; the SetupIntent webhook is usually instant.
            let tries = 0;
            const poll = setInterval(async () => {
                tries += 1;
                await load();
                if (tries >= 5) clearInterval(poll);
            }, 1500);
            // Clean the URL.
            navigate(location.pathname, { replace: true });
            return () => clearInterval(poll);
        }
    }, [location, load, navigate]);

    const setDefault = async (pmId) => {
        setBusy(true);
        try {
            await api.post(`/customer/payment-methods/${pmId}/set-default`);
            toast.success("Default bank account updated");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (pm) => {
        const ok = window.confirm(
            `Remove ${pm.bank_name || "this bank"} ••••${pm.last4}? Any recurring schedules using this account will be reverted to manual payment.`
        );
        if (!ok) return;
        setBusy(true);
        try {
            await api.delete(`/customer/payment-methods/${pm.id}`);
            toast.success("Bank account removed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <DashboardShell>
            <Link
                to="/customer"
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"
            >
                <ChevronLeft size={14} /> Dashboard
            </Link>
            <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                Billing
            </p>
            <h1
                data-testid="payment-methods-heading"
                className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            >
                Payment methods
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                Save bank accounts here to pay invoices faster and (when enabled
                by your admin) allow recurring rent to be charged automatically.
                Powered by Stripe — we never see your account number.
            </p>

            <Card className="mt-6">
                <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <h2 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                            <CreditCard size={16} className="text-slate-500" />
                            Saved bank accounts
                        </h2>
                        <Button
                            onClick={() => setShowAdd(true)}
                            data-testid="payment-methods-add-cta"
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        >
                            <Plus size={14} /> Add bank account
                        </Button>
                    </div>

                    {loading && (
                        <p className="text-sm text-slate-500 inline-flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> Loading bank accounts…
                        </p>
                    )}

                    {!loading && error && (
                        <div
                            data-testid="payment-methods-error"
                            className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 flex items-start gap-2"
                        >
                            <AlertCircle size={14} className="mt-0.5 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {!loading && !error && items.length === 0 && (
                        <div
                            data-testid="payment-methods-empty"
                            className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center"
                        >
                            <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                                <Landmark size={20} />
                            </div>
                            <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
                                No bank accounts saved yet
                            </h3>
                            <p className="mt-1 max-w-sm mx-auto text-sm text-slate-600">
                                Add a US bank account to pay future invoices faster.
                                Verification typically takes seconds.
                            </p>
                            <Button
                                onClick={() => setShowAdd(true)}
                                data-testid="payment-methods-empty-cta"
                                className="mt-4 bg-blue-900 hover:bg-blue-800 gap-1.5"
                            >
                                <Plus size={14} /> Add your first bank account
                            </Button>
                        </div>
                    )}

                    {!loading && !error && items.length > 0 && (
                        <div className="space-y-3" data-testid="payment-methods-list">
                            {items.map((pm) => (
                                <PMRow
                                    key={pm.id}
                                    pm={pm}
                                    isDefault={pm.id === defaultId}
                                    onSetDefault={setDefault}
                                    onRemove={remove}
                                    busy={busy}
                                />
                            ))}
                        </div>
                    )}

                    <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-3 text-xs text-slate-600 leading-relaxed flex items-start gap-2">
                        <ShieldCheck size={14} className="mt-0.5 text-emerald-700 shrink-0" />
                        <span>
                            Bank account details are stored by <strong>Stripe</strong>,
                            our PCI-compliant payments processor. RentFlow only
                            retains the bank name and last 4 digits for display.
                        </span>
                    </div>
                </CardContent>
            </Card>

            <AddBankAccountModal
                open={showAdd}
                onOpenChange={setShowAdd}
                onInitiated={() => {}}
            />
        </DashboardShell>
    );
}
